import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import TranslateIcon from '@mui/icons-material/Translate';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { HttpService } from 'core/services/http';
import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminFooter from './AdminFooter';
import AdminHeader from './AdminHeader';

const drawerWidth = 232;
const navItems = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/cms/dashboard' },
  { label: 'Jobs', icon: <WorkOutlineIcon />, path: '/cms/job' },
  { label: 'Companies', icon: <BusinessIcon />, path: '/cms/company' },
  { label: 'CVs', icon: <DescriptionIcon />, path: '/cms/cv' },
  { label: 'Users', icon: <PeopleAltIcon />, path: '/cms/user' },
  { label: 'Applications', icon: <AssignmentTurnedInIcon />, path: '/cms/applications' },
  { label: 'Settings', icon: <SettingsApplicationsIcon />, path: '/cms/settings' }
];

const AdminLayout = ({ children, pageTitle }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const user = useMemo(() => HttpService.getUser?.() ?? null, []);

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
        <Typography variant="caption" fontWeight={700} sx={{ letterSpacing: '0.12em', color: 'primary.main' }}>
          JOB FLOW CONNECT
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ mt: 0.25 }}>
          Admin Console
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {user?.email ?? 'Signed in'}
        </Typography>
      </Box>
      <Divider sx={{ mx: 2 }} />
      <List sx={{ flexGrow: 1, px: 1.5, py: 1.5 }}>
        {navItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          return (
            <ListItemButton
              key={item.path}
              selected={isActive}
              onClick={() => handleNavigate(item.path)}
              sx={(theme) => ({
                py: 0.9,
                px: 1.5,
                mb: 0.5,
                color: 'text.secondary',
                '&:hover': { bgcolor: 'action.hover', color: 'text.primary', transform: 'translateX(2px)' },
                '&.Mui-selected, &.Mui-selected:hover': {
                  color: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.14),
                  boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.28)}, 0 6px 22px -10px ${alpha(theme.palette.primary.main, 0.7)}`
                }
              })}
            >
              <ListItemIcon sx={{ minWidth: 34, color: 'inherit', '& svg': { fontSize: 19 } }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.83rem', fontWeight: isActive ? 650 : 500 }} />
            </ListItemButton>
          );
        })}
      </List>
      <Box sx={{ px: 2.5, py: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Need help? support@jobflowconnect.com
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'transparent' }}>
      <AdminHeader
        onMenuClick={handleDrawerToggle}
        user={user}
        pageTitle={pageTitle}
        drawerWidth={drawerWidth}
      />
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }} aria-label="admin navigation">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box component="main" sx={{ flexGrow: 1, width: { md: `calc(100% - ${drawerWidth}px)` }, display: 'flex', flexDirection: 'column' }}>
        <Toolbar sx={{ minHeight: '56px !important' }} />
        <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, pt: { xs: 2.5, md: 3 } }}>
          {children}
        </Box>
        <AdminFooter />
      </Box>
    </Box>
  );
};

export default AdminLayout;